/**
 * v2 KpiTile — large stat tile used in Dashboard, Failures, Rollouts headers.
 *
 * Layout: small uppercase label · big numeric value (display serif) · optional
 * trend delta · optional inline sparkline child. Background card-styled.
 */

import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

interface KpiTileProps {
  label: string;
  /** Pre-formatted value (e.g. "1,284" or "97.8%"). Use serif display font. */
  value: ReactNode;
  /** Optional secondary line (description or context). */
  hint?: ReactNode;
  /** Optional inline content rendered to the right of the value (e.g. sparkline). */
  accessory?: ReactNode;
  /** Optional trend delta — pass `<TrendDelta value={...} />`. */
  trend?: ReactNode;
  /** Optional click handler — turns tile into a button. */
  onClick?: () => void;
  className?: string;
}

export function KpiTile({
  label,
  value,
  hint,
  accessory,
  trend,
  onClick,
  className,
}: KpiTileProps) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={cn(
        "flex flex-col rounded-lg border border-border bg-card p-4 text-left shadow-[var(--v2-shadow-sm)]",
        onClick && "transition-colors hover:bg-secondary",
        className
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10.5px] font-semibold uppercase tracking-[0.05em] text-[var(--v2-fg-subtle)]">
          {label}
        </span>
        {trend}
      </div>
      <div className="mt-2 flex items-end justify-between gap-3">
        <span className="font-display tnum text-[30px] leading-none text-foreground">{value}</span>
        {accessory && <div className="shrink-0">{accessory}</div>}
      </div>
      {hint !== undefined && (
        <div className="mt-2 text-[11.5px] text-muted-foreground">{hint}</div>
      )}
    </Tag>
  );
}
